// Configuration et utilitaires Google Analytics 4
import { analytics } from './firebase';

// ID de mesure Google Analytics
const GA_MEASUREMENT_ID = 'G-JP99R58XZ2';

// Types pour les événements personnalisés
export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

export interface PageViewEvent {
  page_title: string;
  page_location: string;
  page_path: string;
}

// Déclaration globale pour gtag
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'consent',
      targetId: string | Date,
      config?: any
    ) => void;
    dataLayer: any[];
  }
}

/**
 * Initialise Google Analytics 4
 * Charge le script GA4 et configure les paramètres de base
 */
export const initializeAnalytics = (): void => {
  try {
    // Vérifier si GA4 est déjà initialisé
    if (typeof window !== 'undefined' && window.gtag) {
      console.log('Google Analytics déjà initialisé');
      return;
    }

    // Créer le script GA4
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialiser dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Configuration de base
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      // Anonymisation des IP pour respecter la vie privée
      anonymize_ip: true,
      // Désactiver la collecte automatique des pages
      send_page_view: false,
      // Configuration des cookies
      cookie_flags: 'SameSite=None;Secure',
      // Durée de vie des cookies (en secondes) - 2 ans
      cookie_expires: 63072000,
    });

    console.log('Google Analytics 4 initialisé avec succès');
  } catch (error) {
    console.error('Erreur lors de l\'initialisation de Google Analytics:', error);
  }
};

/**
 * Envoie un événement de page vue
 * @param pageData - Données de la page visitée
 */
export const trackPageView = (pageData: PageViewEvent): void => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      // Forcer l'envoi d'un page_view avec les données spécifiques
      window.gtag('event', 'page_view', {
        page_title: pageData.page_title,
        page_location: pageData.page_location,
        page_path: pageData.page_path,
        // Ajouter un timestamp pour forcer la distinction
        custom_parameter: Date.now()
      });
      
      console.log('📊 GA4 Page View envoyé:', {
        title: pageData.page_title,
        path: pageData.page_path,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Erreur lors du tracking de page vue:', error);
  }
};

/**
 * Envoie un événement personnalisé
 * @param event - Données de l'événement
 */
export const trackEvent = (event: AnalyticsEvent): void => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
      });
      
      console.log('Événement tracké:', event);
    }
  } catch (error) {
    console.error('Erreur lors du tracking d\'événement:', error);
  }
};

/**
 * Événements prédéfinis pour l'application SimulReno
 */
export const AnalyticsEvents = {
  // Navigation
  NAVIGATION: {
    HOME_VISIT: { action: 'visit_home', category: 'navigation' },
    QUICK_ESTIMATE_START: { action: 'start_quick_estimate', category: 'navigation' },
    DETAILED_ESTIMATE_START: { action: 'start_detailed_estimate', category: 'navigation' },
    DASHBOARD_VISIT: { action: 'visit_dashboard', category: 'navigation' },
  },

  // Authentification
  AUTH: {
    REGISTER_START: { action: 'register_start', category: 'auth' },
    REGISTER_SUCCESS: { action: 'register_success', category: 'auth' },
    LOGIN_START: { action: 'login_start', category: 'auth' },
    LOGIN_SUCCESS: { action: 'login_success', category: 'auth' },
    LOGOUT: { action: 'logout', category: 'auth' },
  },

  // Estimations
  ESTIMATES: {
    QUICK_COMPLETE: { action: 'quick_estimate_complete', category: 'estimates' },
    DETAILED_COMPLETE: { action: 'detailed_estimate_complete', category: 'estimates' },
    ESTIMATE_SAVE: { action: 'estimate_save', category: 'estimates' },
    ESTIMATE_EXPORT_PDF: { action: 'estimate_export_pdf', category: 'estimates' },
    ESTIMATE_VIEW: { action: 'estimate_view', category: 'estimates' },
    ESTIMATE_EDIT: { action: 'estimate_edit', category: 'estimates' },
  },

  // Crédits et paiements
  CREDITS: {
    PURCHASE_START: { action: 'credit_purchase_start', category: 'credits' },
    PURCHASE_SUCCESS: { action: 'credit_purchase_success', category: 'credits' },
    PURCHASE_FAILED: { action: 'credit_purchase_failed', category: 'credits' },
  },

  // Interactions
  INTERACTIONS: {
    CTA_CLICK: { action: 'cta_click', category: 'interactions' },
    EXTERNAL_LINK: { action: 'external_link_click', category: 'interactions' },
    CONTACT_FORM: { action: 'contact_form_submit', category: 'interactions' },
    TESTIMONIAL_VIEW: { action: 'testimonial_view', category: 'interactions' },
  },

  // Erreurs
  ERRORS: {
    FORM_ERROR: { action: 'form_error', category: 'errors' },
    API_ERROR: { action: 'api_error', category: 'errors' },
    PAYMENT_ERROR: { action: 'payment_error', category: 'errors' },
  }
};

/**
 * Fonctions utilitaires pour les événements courants
 */
export const trackCTAClick = (ctaName: string, location: string): void => {
  trackEvent({
    ...AnalyticsEvents.INTERACTIONS.CTA_CLICK,
    label: `${ctaName}_${location}`,
  });
};

export const trackFormSubmission = (formName: string, success: boolean): void => {
  trackEvent({
    action: success ? 'form_submit_success' : 'form_submit_error',
    category: 'forms',
    label: formName,
  });
};

export const trackEstimateStep = (stepName: string, estimateType: 'quick' | 'detailed'): void => {
  trackEvent({
    action: 'estimate_step_complete',
    category: 'estimates',
    label: `${estimateType}_${stepName}`,
  });
};

export const trackTimeOnPage = (pageName: string, timeInSeconds: number): void => {
  trackEvent({
    action: 'time_on_page',
    category: 'engagement',
    label: pageName,
    value: timeInSeconds,
  });
};

/**
 * Gestion du consentement RGPD
 */
export const updateConsentSettings = (hasConsent: boolean): void => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: hasConsent ? 'granted' : 'denied',
        ad_storage: 'denied', // Toujours refuser les publicités
      });
      
      console.log('Consentement mis à jour:', hasConsent ? 'accordé' : 'refusé');
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour du consentement:', error);
  }
};

/**
 * Désactive complètement Google Analytics
 */
export const disableAnalytics = (): void => {
  try {
    // Définir la variable globale pour désactiver GA
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    console.log('Google Analytics désactivé');
  } catch (error) {
    console.error('Erreur lors de la désactivation d\'Analytics:', error);
  }
};